/*
 *
 *  * Copyright(c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { WorkList } from '@app/data-model/worklist-navigator/work-list/work-list';
import { appConstants } from '@app/common/app-constants';
import { ToDoList, ToDoListId } from '@app/data-model/list/to-do-list';
import { DataAccessService } from '@app/common/data-access.service';
import { Logger, MessageService } from '@core/lib/services';
import { AppConfigService } from '@app/common/app-config.service';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { UserPreferenceService } from '@app/common/user-preference.service';
import { PinnedBarService } from '@app/ui/pinned-bar/pinned-bar.service';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToDoCountService } from '@app/ui/common/work-list-navigator/to-do-count.service';
import { Count } from '@core/lib/components/to-do-count/count';
import { ExamCacheService } from '@app/ui/client-cache/exam-cache.service';
import { Events, EventService } from '@app/common/event';
import { ProxyDataService } from '@app/core/http';
import { DiConfigService } from '@app/di/di-config.service';

const log = new Logger('ToDoListService');

@Injectable({
  providedIn: 'root'
})
export class ToDoListService {
  private toDoWorkLists: ToDoList[];

  private toDoWorkListObserver: BehaviorSubject<ToDoList[]> = new BehaviorSubject([]);

  constructor(
    private dataAccess: DataAccessService,
    private dataService: ProxyDataService,
    private appConfigService: AppConfigService,
    private userPrivilegesService: UserPrivilegesService,
    private userPreferenceService: UserPreferenceService,
    private pinnedBarService: PinnedBarService,
    private toDoCountService: ToDoCountService,
    private examCacheService: ExamCacheService,
    private translate: TranslateService,
    private messageService: MessageService,
    private eventService: EventService,
    private diConfigService: DiConfigService
  ) {
    this.registerEvents();
  }

  prepareToDoWorkLists(workLists: WorkList[]): ToDoList[] {
    this.toDoWorkLists = [];
    const nonDictate =
      this.diConfigService.DI_CONFIG.REPORTING_INTEGRATION_TYPE === appConstants.reportingAppName.None ||
      !this.userPrivilegesService.hasPrivilege('Dictate');
    appConstants.systemWorkListDescription.forEach(config => {
      config.displayName = this.translate.instant(config.displayName);
      this.addToDoWorkList(workLists, config, nonDictate);
    });

    if (this.toDoWorkLists.length) {
      this.toDoCountService.setList(this.toDoWorkLists);
      this.toDoWorkListObserver.next(this.toDoWorkLists);
      //change to observer pattern
      this.messageService.broadcast('todoworklists', workLists);
    }
    return this.toDoWorkLists;
  }

  /*getToDoWorkListsObserver(): Observable<ToDoList[]> {
    return this.toDoWorkListObserver.asObservable();
  }*/

  getToDoListById(id): ToDoList {
    return this.toDoWorkLists.find(list => {
      return list.id === id;
    });
  }

  populateNextPage(workList: ToDoList, start = 1) {
    //check reserved or not to implement the filter
    const params = (workList.buildParams && workList.buildParams(this.getBuildParamsConfig(workList.id))) || {};
    params.pageStart = start;
    params.pageEnd = start + this.appConfigService.getWorklistPageSize() - 1;
    if (workList.isFiltered) {
      const additionalFilter = _.extend({}, workList.filterData);
      params.filter = [params.filter, additionalFilter];
    }
    if (workList.isSorted) {
      params.sort = workList.sortData;
    }

    return new Promise((resolve, reject) => {
      const workItemPromise = this.dataAccess.getWorkItems('worklists', workList.endPointId, params);
      workItemPromise
        .then((data: any) => {
          if (!data.items) {
            resolve(undefined);
            if (workList.isFiltered) {
              workList.count = new Count(0, 0, 0);
            }
            return;
          }
          resolve(data.items);
          if (workList.id === ToDoListId.ASSIGNED) {
            _.each(data.items, item => {
              this.examCacheService.setAssignedExamCache(item.id);
            });
          }
        })
        .catch(error => {
          log.error(
            `Error occurred while getting work items for to-do list, WorkListId - ${
              workList.endPointId
            }, Exception - ${JSON.stringify(error)}`
          );
          reject(error);
        });
    });
  }

  private addToDoWorkList(workLists: WorkList[], config: any, nonDictate: boolean) {
    const regex = new RegExp('^' + config.description + '$', 'i');
    const workList = _.find(workLists, workList => {
      return regex.test(workList.description);
    });
    if (workList) {
      let displayToDoList = true;
      const toDoList = new ToDoList(
        workList,
        _.extend(config, {
          currentUser: this.appConfigService.getUserId(),
          pageSize: this.appConfigService.getWorklistPageSize()
        })
      );

      const displayNawWorklists =
        this.appConfigService.getAppConfigByKey('nativeAcademicWorkflow') === 'true' &&
        this.userPrivilegesService.hasPrivilege('ID_Assignment') &&
        this.userPrivilegesService.hasPrivilege('Dictate');

      switch (toDoList.id) {
        case ToDoListId.ASSIGNED: {
          this.toDoCountService.setWorkItemStateWorklistId(toDoList.worklistId);
          break;
        }
        case ToDoListId.ATTENDING: {
          this.toDoCountService.setAttendingReadWorklistId(toDoList.worklistId);
          displayToDoList =
            this.userPrivilegesService.hasPrivilege('ID_AttendingReviewWL') &&
            this.appConfigService.getAppConfigByKey('attendingReview') === 'true';
          break;
        }
        case ToDoListId.UNSIGNED: {
          this.toDoCountService.setReportStateWorklistId(toDoList.worklistId);
          break;
        }
        case ToDoListId.TAB: {
          this.examCacheService.setTabWorklistId(toDoList.worklistId);
          toDoList.count.isUnmonitored = true;
          displayToDoList = this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true';
          break;
        }
        case ToDoListId.RECENTLY_VIEWED: {
          toDoList.count.isUnmonitored = true;
          break;
        }
        case ToDoListId.EXCEPTION: {
          toDoList.count.isUnmonitored = true;
          displayToDoList = this.userPrivilegesService.hasPrivilege('ID_ExceptionWL');
          this.examCacheService.setExceptionWorklistId(toDoList.worklistId);
          break;
        }
        case ToDoListId.NAW_COREPORT: {
          this.toDoCountService.setNawCoreportWorklistId(toDoList.worklistId);
          toDoList.count.isUnmonitored = false;
          displayToDoList = displayNawWorklists;
          break;
        }
        case ToDoListId.NAW_REVIEWREQUESTED: {
          this.toDoCountService.setNawReviewRequestedWorklistId(toDoList.worklistId);
          toDoList.count.isUnmonitored = false;
          displayToDoList = displayNawWorklists;
          break;
        }
        default:
          break;
      }

      if (!(config.id === ToDoListId.UNSIGNED && nonDictate)) {
        if (!displayToDoList) {
          toDoList.isPinned = false;
          toDoList.isHidden = true;
        }

        toDoList.isPinned && this.pinnedBarService.addWorkListToPinnedBar(toDoList);
        //update worklist Display name used for Layouts screen
        workList.name = toDoList.displayName;

        this.toDoWorkLists.push(toDoList);
      }
    }
  }

  private getBuildParamsConfig(id): any {
    const currentContextPtId = this.appConfigService.getGroupByPatientContext()
      ? this.appConfigService.getGroupByPatientContext().groupByCurrentContextPatientId
      : null;

    const config = {
      currentUser: this.appConfigService.getUserId(),
      currentContextPtId: currentContextPtId,
      currentPatientGroupingIndex: ''
    } as any;

    if (id === ToDoListId.UNSIGNED) {
      config.ID_AttendingReviewWL = this.userPrivilegesService.hasPrivilege('ID_AttendingReviewWL');
      config.attendingReview = this.appConfigService.getAppConfigByKey('attendingReview');
      config.unsignedFilter = this.userPreferenceService.getUserPreferenceFromCache('unsigned-filter');
    }
    return config;
  }

  isToDoListActive(id: string) {
    return (
      (id !== ToDoListId.ATTENDING ||
        (this.userPrivilegesService.hasPrivilege('ID_AttendingReviewWL') &&
          this.appConfigService.getAppConfigByKey('attendingReview') === 'true')) &&
      (id !== ToDoListId.TAB || this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true')
    );
  }

  private saveRecentExam(requestedProcedureId, targets) {
    if (requestedProcedureId) {
      const params = { target: targets };
      this.dataService.update('recent-exams/save', requestedProcedureId, params).then(
        () => {
          log.info('Save Recent Exams  ' + requestedProcedureId);
          if (_.contains(targets, ToDoListId.TAB)) {
            this.examCacheService.setTabExamCache(requestedProcedureId);
            const activeWorklist = this.appConfigService.getActiveList();
            if (activeWorklist && activeWorklist.id === ToDoListId.TAB) {
              this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
            }
          }
        },
        error => {
          log.error('[saveRecentExam] - Error occrred while saving the recent exam, ERROR - ', JSON.stringify(error));
        }
      );
    }
  }

  private registerEvents() {
    this.eventService.afterEvent(Events.launchreadonly.name, target => {
      if (!_.isUndefined(target.keys) && target.keys.source === 'desktop-integration') {
        return;
      }
      //Blocking remote exams to recently viewed exam
      if (
        !_.isUndefined(target.keys) &&
        target.keys.repositoryType !== undefined &&
        target.keys.repositoryType !== 'Local'
      ) {
        return;
      }
      const targets =
        (!_.isUndefined(target.keys) &&
          (target.keys.source === 'diagnostic-hub' ||
            (target.keys.source === 'patient-history' && target.keys.comparisonAction === 'viewImages'))) ||
        this.examCacheService.getTabExamCache(target.id) ||
        this.appConfigService.getAppConfigByKey('unlimitedTabs') !== 'true'
          ? ['recentexams']
          : [ToDoListId.TAB, 'recentexams'];
      if (target.keys.disableAutoLaunchViewer === false) {
        this.saveRecentExam(target.id, targets);
      } else if (_.contains(targets, ToDoListId.TAB)) {
        this.saveRecentExam(target.id, [ToDoListId.TAB]);
      }
    });
  }
}
